import { Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Menu as ChakraMenu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getWalletTransactions, syncWalletTransactions } from '../../../api/multisigAPI';
import { IncomingTransactionType } from '../../../types/transactions';
import { ReactComponent as ArrowsIconGreen } from 'assets/arrows-icon-green.svg';
import { GlobalContext } from '../../../providers/Global';
import { SCREEN_LG, SCREEN_SM } from '../../../utils/constants';
import { useMediaQuery } from 'react-responsive';
import { ApplicationRoutes } from 'utils/routes';
import ActivityItem from './ActivityItem';
import ActivityHeader from './ActivityHeader';

const ITEMS_PER_PAGE = 10;
const PREVIEW_LIMIT = 5;

const typeFilters = [
  { label: 'All', value: '' },
  { label: 'HBAR', value: 'hbar' },
  { label: 'Token', value: 'token' },
  { label: 'NFT', value: 'nft' },
  { label: 'Airdrop', value: 'airdrop' },
  { label: 'Fund', value: 'Fund' },
];

interface ActivityListProps {
  isPreview?: boolean;
}

const ActivityList = ({ isPreview = false }: ActivityListProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const expandId = searchParams.get('expand');
  const { walletId, multisigAccountId } = useParams();
  const [transactions, setTransactions] = useState<IncomingTransactionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTypeFilter, setActiveTypeFilter] = useState('');
  const { refreshKey } = useContext(GlobalContext);

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });
  const isScreenLarge = useMediaQuery({ query: `(max-width: ${SCREEN_LG})` });

  const isActivityPage = location.pathname.includes('activity');

  const limit = isPreview ? PREVIEW_LIMIT : ITEMS_PER_PAGE;

  const fetchActivity = useCallback(
    async (
      page: number = 1,
      order?: 'asc' | 'desc',
      typeFilter?: string,
    ) => {
      if (walletId) {
        setIsLoading(true);
        try {
          const result = await getWalletTransactions(
            walletId,
            page,
            limit,
            'date',
            order ?? sortOrder,
            (typeFilter ?? activeTypeFilter) || undefined,
            undefined,
            'incoming',
          );
          if (result?.data) {
            setTransactions(result.data.transactions);
            setTotalPages(result.data.totalPages);
            setTotal(result.data.total);
            setCurrentPage(result.data.page);
          }
        } catch {
          // Network error — keep existing data visible
        } finally {
          setIsLoading(false);
        }
      }
    },
    [walletId, limit, sortOrder, activeTypeFilter],
  );

  // Sync incoming transactions on mount / wallet change — ensures fresh Mirror Node data
  const lastSyncedWalletId = useRef<string | null>(null);
  useEffect(() => {
    if (!walletId || lastSyncedWalletId.current === walletId) return;
    lastSyncedWalletId.current = walletId;

    const controller = new AbortController();
    syncWalletTransactions(walletId, controller.signal).catch(() => {});

    return () => controller.abort();
  }, [walletId]);

  useEffect(() => {
    if (walletId) {
      fetchActivity(1);
    }
  }, [walletId, fetchActivity, refreshKey]);

  const handleSortToggle = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);
    fetchActivity(1, newOrder);
  };

  const handleTypeFilter = (value: string) => {
    setActiveTypeFilter(value);
    fetchActivity(1, sortOrder, value);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchActivity(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchActivity(currentPage + 1);
    }
  };

  const activeTypeLabel = typeFilters.find((f) => f.value === activeTypeFilter)?.label || 'All';

  const filterButtonStyle = {
    border: '1px solid',
    borderColor: 'blackAlpha.200',
    cursor: 'pointer',
    bg: 'white',
    transition: 'all 0.15s',
    _hover: { borderColor: 'blackAlpha.400' },
  };

  const renderFilterDropdowns = () => (
    <Flex gap={3} mb={2} flexWrap="wrap">
      <ChakraMenu placement="bottom-start">
        <MenuButton
          as={Flex}
          alignItems="center"
          gap={1}
          px={3}
          py={1.5}
          borderRadius={6}
          sx={{ fontSize: '12px' }}
          {...filterButtonStyle}
        >
          <Text as="span" color="blackAlpha.500" fontSize="12px" mr={1}>Type:</Text>
          <Text as="span" fontWeight="600" fontSize="12px">{activeTypeLabel}</Text>
          <Text as="span" fontSize="10px" color="blackAlpha.400" ml={1}>&#9662;</Text>
        </MenuButton>
        <MenuList minW="180px" py={2} borderRadius={8} boxShadow="lg" zIndex={10}>
          {typeFilters.map((filter) => (
            <MenuItem
              key={filter.value}
              fontSize="sm"
              py={2}
              fontWeight={activeTypeFilter === filter.value ? '600' : '400'}
              bg={activeTypeFilter === filter.value ? 'blackAlpha.50' : 'transparent'}
              _hover={{ bg: 'blackAlpha.100' }}
              onClick={() => handleTypeFilter(filter.value)}
            >
              {filter.label}
            </MenuItem>
          ))}
        </MenuList>
      </ChakraMenu>

      <Flex
        as="button"
        alignItems="center"
        gap={1}
        px={3}
        py={1.5}
        borderRadius={6}
        onClick={handleSortToggle}
        {...filterButtonStyle}
      >
        <Text as="span" color="blackAlpha.500" fontSize="12px" mr={1}>Date:</Text>
        <Text as="span" fontWeight="600" fontSize="12px">
          {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
        </Text>
        <Text as="span" fontSize="10px" color="blackAlpha.400" ml={1}>
          {sortOrder === 'desc' ? '↓' : '↑'}
        </Text>
      </Flex>
    </Flex>
  );

  if (isLoading && transactions.length === 0) {
    if (isPreview) {
      return null;
    }
    return (
      <Box textAlign="center" mt="120px">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (total === 0 && !isLoading && !activeTypeFilter) {
    if (isPreview) {
      return null;
    }
    return (
      <Flex flexDirection="column" alignItems="center" mt={5}>
        <ArrowsIconGreen />
        <Text size="2xl" variant="bodyBold">
          No activity yet.
        </Text>
        <Text mt={1} size="xl">
          Incoming transactions will appear here.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      flex={1}
      p={6}
      pt={isScreenSmall ? 4 : 6}
      bg="white"
      borderRadius={16}
      flexDirection="column"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
    >
      {isPreview && (
        <Text
          size="lg"
          fontWeight={300}
          color="blackAlpha.500"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          Recent Activity
          {isScreenLarge && (
            <ChakraLink
              as={RouterLink}
              to={`${ApplicationRoutes.Dashboard}/${walletId}/activity/${multisigAccountId}`}
              cursor="pointer"
              fontSize="sm"
              fontWeight="500"
              letterSpacing="0.5px"
              color="blackAlpha.900"
              textDecoration="none !important"
            >
              View
            </ChakraLink>
          )}
        </Text>
      )}

      {/* Filter dropdowns — only on full Activity page */}
      {isActivityPage && !isPreview && renderFilterDropdowns()}

      <ActivityHeader />

      <Box display="flex" width="full" flexDirection="column" position="relative">
        {isLoading && transactions.length > 0 && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="whiteAlpha.700"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1}
            borderRadius={8}
          >
            <Spinner size="md" />
          </Box>
        )}
        {transactions.length > 0 ? (
          transactions.map((transaction: IncomingTransactionType) => (
            <ActivityItem
              key={transaction._id}
              transaction={transaction}
              defaultOpen={expandId === transaction._id}
            />
          ))
        ) : !isLoading && isActivityPage && activeTypeFilter ? (
          <Flex flexDirection="column" alignItems="center" py={10} gap={2}>
            <Text size="md" color="blackAlpha.500">
              No activity matches the selected filter.
            </Text>
            <Box
              as="button"
              fontSize="sm"
              color="blackAlpha.700"
              textDecoration="underline"
              onClick={() => {
                setActiveTypeFilter('');
                fetchActivity(1, sortOrder, '');
              }}
            >
              Clear filter
            </Box>
          </Flex>
        ) : null}
      </Box>

      {/* Pagination — only on full page */}
      {!isPreview && totalPages > 1 && (
        <Flex
          justify="space-between"
          align="center"
          mt={4}
          pt={4}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <Text size="xs" color="blackAlpha.600">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{' '}
            {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total}
          </Text>
          <Flex gap={2} align="center">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrevPage}
              isDisabled={currentPage === 1}
              borderRadius={6}
            >
              Previous
            </Button>
            <Text size="sm" fontWeight="500" px={2}>
              {currentPage} / {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={handleNextPage}
              isDisabled={currentPage === totalPages}
              borderRadius={6}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default ActivityList;
